"use client";

import React, { useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  styled,
  Box,
  Button,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

const StyledAccordion = styled(Accordion)(({ theme }) => ({
  boxShadow: "none",
  border: "1px solid #E2E8F0",
  borderRadius: "13px !important",
  marginBottom: "16px !important",
  "&:before": { display: "none" },
  background: "#FFFFFF",
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  padding: theme.spacing(2.5, 3),
  minHeight: "auto",
  "&.Mui-expanded": { minHeight: "auto" },
  "& .MuiAccordionSummary-content": {
    margin: 0,
    "&.Mui-expanded": { margin: 0 },
  },
  "& .MuiAccordionSummary-expandIconWrapper": { color: "#64748B" },
}));

const StyledAccordionDetails = styled(AccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0, 3, 2.5, 3),
  paddingTop: 0,
}));

const INITIAL_VISIBLE_COUNT = 5;

const LeapFAQs = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";

  const faqsRaw = t("leapFaqs.faqs", { returnObjects: true });
  const faqs = Array.isArray(faqsRaw) ? faqsRaw : [];

  const [expanded, setExpanded] = useState(0);
  const [showAll, setShowAll] = useState(false);

  const handleChange = (index) => (event, isExpanded) => {
    setExpanded(isExpanded ? index : false);
  };

  const hasMore = faqs.length > INITIAL_VISIBLE_COUNT;
  const visibleFaqs = showAll ? faqs : faqs.slice(0, INITIAL_VISIBLE_COUNT);

  const handleToggleShowAll = () => {
    // If collapsing back, also close any expanded accordion that's now hidden
    if (showAll && expanded !== false && expanded >= INITIAL_VISIBLE_COUNT) {
      setExpanded(false);
    }
    setShowAll((prev) => !prev);
  };

  return (
    <Box id="faq-section" sx={{ direction: isArabic ? "rtl" : "ltr" ,    scrollMarginTop: { xs: "70px", lg: "90px" },
}}>
      <Container
        maxWidth="md"
        component={motion.div}
        initial="hidden"
        sx={{
          py: 8,
          px: { xs: 2, sm: 3 },
        }}
      >
        {/* Heading Section */}
        <Box
          sx={{
            textAlign: "center",
            mb: 6,
          }}
        >
          <Typography
            variant="h3"
            component="h2"
            fontWeight="700"
            sx={{
              fontSize: { xs: "1.7rem", sm: "2rem", md: "2.2rem" },
              color: "#111827",
              fontFamily: "var(--font-plusjakarta)",
            }}
          >
            {t("leapFaqs.title")}
          </Typography>
        </Box>

        {/* Accordion Section */}
        <AnimatePresence>
          {visibleFaqs.map((faq, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <StyledAccordion
                disableGutters
                elevation={0}
                expanded={expanded === index}
                onChange={handleChange(index)}
              >
                <StyledAccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`leap-faq-panel${index}-content`}
                  id={`leap-faq-panel${index}-header`}
                >
                  <Typography
                    sx={{
                      fontWeight: 400,
                      fontFamily: "var(--font-outfit)",
                      fontSize: { xs: "0.95rem", sm: "1rem", md: "1.05rem" },
                      color: "#111827",
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {faq.question}
                  </Typography>
                </StyledAccordionSummary>
                <StyledAccordionDetails>
                  <Typography
                    sx={{
                      fontFamily: "var(--font-outfit)",
                      fontSize: { xs: "0.9rem", sm: "0.95rem", md: "1rem" },
                      color: "#64748B",
                      fontWeight: 400,
                      lineHeight: 1.7,
                      textAlign: isArabic ? "right" : "left",
                    }}
                  >
                    {faq.answer}
                  </Typography>
                </StyledAccordionDetails>
              </StyledAccordion>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* Show More / Show Less Button */}
        {hasMore && (
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Button
              onClick={handleToggleShowAll}
              sx={{
                textTransform: "none",
                fontFamily: "var(--font-outfit)",
                fontWeight: 600,
                fontSize: "0.95rem",
                color: "#111827",
                px: 3,
                py: 1,
                borderRadius: "20px",
                border: "1px solid #E2E8F0",
                display: "inline-flex",
                justifyContent: "center",
                alignItems: "center",
                "&:hover": {
                  background: "#F5F3FF",
                  borderColor: "#DDD6FE",
                },
                // MUI ka default asymmetric icon margin override karo
                "& .MuiButton-endIcon": {
                  marginLeft: isArabic ? 0 : "8px",
                  marginRight: isArabic ? "8px" : 0,
                },
              }}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    color: "#111827",
                    transform: showAll ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.3s",
                  }}
                />
              }
            >
              {showAll
                ? t("leapFaqs.showLess", "Show Less")
                : t("leapFaqs.showMore", "Show More")}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
};

export default LeapFAQs;